/** Vault page (on-chain vault per asset, owner actions signed in the owner's wallet), plus the bits other pages show about the vault. */
export const vault = {
  // Navigation, permissions, errors (kept here so other areas stay untouched)
  nav: "Vault",
  permPage: "Vault",
  permHelp: {
    "vault:view": "Vault page (on-chain limits, float, cold wallet); prepare owner transactions",
    "vault:edit": "Automatic sweeps to the cold wallet",
  },
  errors: {
    over_vault_limit: "This payout is above the vault's per-payout limit, which the vault owner sets on-chain.",
  },

  // Page
  title: "Vault",
  intro: "Your vault contract holds the funds for payouts. Your own wallet owns it: you set the limits, the float and the cold wallet. iCryptoNow can only send approved payouts within those limits.",
  noVaultsTitle: "No vault yet",
  noVaultsBody: "Vaults appear here once a chain and token are set up for {merchant}. Our team does this during onboarding.",
  sectionLabel: "{token} on {chain}",
  vaultAddress: "Vault",
  ownerAddress: "Owner",
  viewOnExplorer: "View on explorer",
  checkedAgo: "Checked {t}",
  notChecked: "Not checked yet",
  readError: "We couldn't read this vault on-chain: {error}",
  unknown: "Unknown",
  statusActive: "Active",
  statusPaused: "Paused",

  // Key figures
  balanceInVault: "Balance in vault",
  float: "Float",
  floatHint: "Kept in the vault for payouts",
  availableInLedger: "Available in ledger",
  pendingPayouts: "Pending payouts",
  inVault: "In vault",
  inVaultHint: "On-chain, in the vault contract",
  pendingSweep: "Pending sweep",
  pendingSweepHint: "Confirmed deposits still in deposit addresses",
  bothCounted: "In vault and Pending sweep are both already counted in Available.",
  sweptWhen: "Swept when an address reaches {threshold} or after {delay}.",
  sweptSeconds: "Swept within seconds.",
  latestBy: "Latest by {time}.",
  hoursN: { one: "{n} hour", other: "{n} hours" },
  minutesN: { one: "{n} minute", other: "{n} minutes" },
  waitingForSweep: { one: "{n} payout waiting for the sweep", other: "{n} payouts waiting for the sweep" },

  // Limits
  limits: "Payout limits",
  perPayoutLimit: "Per payout",
  dailyLimit: "Per day",
  noLimit: "No limit",
  usedToday: "{used} of {limit} {token} used today, {left} left (resets 00:00 UTC)",
  usedTodayNoLimit: "{used} {token} sent today",
  limitsCountFee: "Limits count the amount plus fee.",
  limitsOffTitle: "Payouts are off until the owner sets limits",
  limitsOffBody: "The vault's per-payout limit is 0, so no payout can be sent from it. The owner sets the limits with their wallet.",

  // Warnings
  warnPaused: "The vault is paused. No payouts or sweeps go out until the owner unpauses it.",
  warnOperator: "Our payout key is not an operator of this vault, so payouts can't be sent. The owner has to add it on-chain; contact iCryptoNow support.",
  warnFloat: "The float ({float}) is smaller than pending payouts ({pending}). Automatic sweeps to the cold wallet pause until it's raised.",
  warnWaiting: { one: "{n} payout is waiting for the vault.", other: "{n} payouts are waiting for the vault." },
  seeWaiting: "See queued payouts",
  warnLowToday: "Only {left} {token} of today's limit is left.",

  // Cold wallet and automatic sweeps
  coldWallet: "Cold wallet",
  notSet: "Not set",
  coldWalletHint: "Set by the owner on-chain. Sweeps can only go to this address.",
  autoSweep: "Automatic sweep to the cold wallet",
  autoSweepHint: "Our payout key moves the excess above the float to your cold wallet. It can't send anywhere else.",
  topUp: "Top up vault",
  topUpTitle: "Top up the {token} vault on {chain}",
  topUpIntro: "Send {token} to this address from any wallet or exchange to fund your payouts. There is no fee, and the amount is added to your balance automatically once confirmed.",
  topUpCap: "Anything above the vault cap ({cap} {token}) is moved back to your cold wallet once a day, so send what you need for about two days of payouts.",
  capTitle: "Vault cap",
  capHint: "To keep large balances out of the vault, anything above {cap} {token} is moved once a day (after 00:10 UTC) to the cold wallet registered on your vault. A cold wallet changed in the last 48 hours is not used.",
  capLast: "Last moved {amount} {token} to your cold wallet {time}.",
  capRefused: "Not moved today: {reason}",
  lowBalance: "The vault holds less than about one day of payouts. Send funds from your cold wallet to the vault address so payouts don't have to wait.",
  modeOff: "Off",
  modeDaily: "Daily",
  modeAbove: "When the excess is large",
  dailyAt: "Daily at {hour}:00 UTC",
  hourLabel: "Hour (UTC)",
  aboveLabel: "When the excess above the float exceeds",
  aboveSummary: "When the excess above the float exceeds {amount} {token}",
  lastRun: "Last run {time}",
  neverRun: "Not run yet",
  skippedNote: "Last run skipped: {note}",
  noColdWalletForSweep: "Set a cold wallet first; sweeps need a destination.",
  saveSweep: "Save sweep settings",
  sweepSaved: "Automatic sweep settings saved",
  thresholdInvalid: "Enter an amount above 0, e.g. 50000.",
  readOnlySweep: "Your role can see these settings but not change them.",

  // Owner actions
  ownerActions: "Owner actions",
  ownerActionsHint: "These change the vault on-chain. You sign them with the owner's own wallet (MetaMask or TronLink). iCryptoNow never sees your keys and never asks for a seed phrase.",
  actions: {
    setLimits: "Set limits",
    setFloat: "Set float",
    setColdWallet: "Set cold wallet",
    withdraw: "Withdraw",
    sweepToCold: "Sweep to cold wallet now",
    pause: "Pause",
    unpause: "Unpause",
  },

  // Action dialog
  dialogIntro: "Vault {vault} · owner {owner}",
  perTxField: "Per-payout limit",
  dailyField: "Daily limit",
  floatField: "Float",
  floatFieldHint: "Kept in the vault for payouts; only the excess is swept to the cold wallet.",
  coldWalletField: "Cold wallet address",
  withdrawTo: "Send to",
  withdrawAmount: "Amount",
  currentValue: "Now: {value}",
  amountInvalid: "Enter an amount like 5000 or 5000.50 ({decimals} decimals at most).",
  dailyBelowPerTx: "The daily limit must be at least the per-payout limit.",
  addressInvalid: "This doesn't look like a {chain} address.",
  withdrawWarnAvailable: "Withdrawing more than Available in ledger ({available} {token}) pauses payouts until it's reconciled.",
  withdrawWarnPending: "After this withdrawal the vault holds less than pending payouts ({pending} {token}); those payouts wait until it's topped up.",
  sweepNeedsCold: "Set a cold wallet before sweeping.",
  prepare: "Review",
  preparing: "Preparing…",

  summary: {
    setLimits: "You are about to set the per-payout limit to {perTx} {token} and the daily limit to {daily} {token} on vault {vault}.",
    setFloat: "You are about to set the float to {amount} {token} on vault {vault}.",
    setColdWallet: "You are about to set the cold wallet of vault {vault} to {address}.",
    withdraw: "You are about to withdraw {amount} {token} from vault {vault} to {address}.",
    sweepToCold: "You are about to move everything above the float from vault {vault} to the cold wallet {address}.",
    pause: "You are about to pause vault {vault}. No payouts or sweeps go out until you unpause it.",
    unpause: "You are about to unpause vault {vault}. Queued payouts start going out again.",
  },
  signWith: "Sign with wallet",
  signing: "Waiting for your wallet…",
  walletEvm: "MetaMask (or another browser wallet)",
  walletTron: "TronLink",
  connectOwner: "Connect the owner wallet {owner} in your wallet and try again. Connected now: {account}.",
  noAccount: "Your wallet didn't share an account. Unlock it and try again.",
  userRejected: "You rejected the request in your wallet.",
  switchChainFailed: "Switch your wallet to the right network ({chainId}) and try again.",
  walletError: "The wallet returned an error: {message}",
  noWalletTitle: "No wallet found in this browser",
  noWalletBody: "Install {wallet} and open this page again, or submit the call below another way (for example from a multisig).",
  rawCall: "Raw call",
  rawTo: "Contract",
  rawFunction: "Function",
  rawArgs: "Arguments",
  rawData: "Data",
  rawSelector: "Function selector",
  rawParameter: "Raw parameter",
  submittedTitle: "Submitted",
  submittedBody: "Submitted. The page updates within about a minute.",
  txHash: "Transaction",
  neverKeys: "We never ask for private keys or seed phrases.",

  // Recent movements
  movements: "Recent vault movements",
  movementsHint: "Money in and out of the vault contract on-chain.",
  colTime: "Time",
  colDirection: "In / out",
  colCounterparty: "Counterparty",
  colAmount: "Amount",
  colTx: "Transaction",
  colStatus: "Status",
  dirIn: "In",
  dirOut: "Out",
  noMovements: "No movements yet",
  movementStatus: { confirmed: "Confirmed", pending: "Pending", flagged: "Flagged" },

  // Other pages
  waitingLabel: "Waiting",
  waitingTitle: "Waiting for the vault",
  waitingBody: "{reason}. It goes out automatically once the vault allows it.",
  overviewLeft: "Today's payout limit: {amount} {token} left",
  overviewLeftLabel: "Today's payout limit left",
  overviewOff: "Payouts off: vault limits not set",
  overviewPendingSweep: "Pending sweep: {amount} {token}",
  /** Self-service networks: the brand names the owner wallet, iCryptoNow deploys the vault and enables the asset. */
  shared: {
    pill: "Shared test vault",
    intro: "Sandbox: every sandbox account uses one shared test vault per network, run by iCryptoNow. Test deposits, callbacks and payouts as usual; your balance is kept in the ledger.",
    note: "This test vault is shared by all sandbox accounts, so its on-chain balance, limits and owner actions are not shown. Move test money in through deposit addresses only, not by sending tokens to the vault. In production your own wallet owns your vault.",
    networkNote: "Set up for the sandbox",
  },
  networks: {
    title: "Networks",
    intro: "Turn on a chain and token for {merchant}. You choose the wallet that will own the vault; we deploy the vault and enable deposits and payouts.",
    emptyLead: "Enable a network below to get your first vault.",
    noNetworks: "No networks are available to enable right now.",
    colNetwork: "Network",
    colFees: "Fees",
    colStatus: "Status",
    colAction: "Action",
    feesInOut: "In {in} · Out {out}",
    minAmounts: "Min. deposit {in} · Min. payout {out}",
    minFees: "Min. fee {in} in · {out} out",
    /** Brands on "rate + network fee" pricing (fee_formula sum). */
    processingFees: "Network fee {in} in · {out} out",
    status: {
      available: "Available",
      pending: "Setting up…",
      deploying: "Setting up…",
      review: "Waiting for iCryptoNow review",
      awaiting_payment: "Waiting for payment",
      failed: "Failed",
      active: "Active",
    },
    enable: "Enable",
    retry: "Try again",
    settingUp: "Setting up…",
    waitingReview: "Waiting for iCryptoNow review",
    failedError: "Last attempt failed: {error}",
    requestedOwner: "Owner {owner}",
    askOwner: "Ask an owner to enable it.",
    setLimitsNext: "Next: set payout limits",
    dialogTitle: "Enable {token} on {chain}",
    dialogIntro: "We deploy a vault for {merchant} on {chain}. Deposits and payouts in {token} start once it's active.",
    perTxLabel: "Per-payout limit",
    dailyLabel: "Daily limit",
    limitsHelp: "Written into your vault when it is created, so payouts work right away. Only your owner wallet can change them later.",
    quickPicks: "Quick picks",
    setupFee: "Setup {amount} USDT",
    setupFree: "Free setup",
    feeLine: "One-time setup fee: {amount} USDT. After you submit, pay it in USDT on {chains}; your vault is created automatically once the payment is confirmed.",
    pay: "Pay",
    payTitle: "Pay the setup fee",
    payIntro: "Your vault for {token} on {chain} is created automatically once the fee is paid.",
    amountDue: "Amount to pay",
    paidSoFar: "Paid so far: {paid} of {fee} USDT",
    payNetwork: "Payment network",
    acceptedTokens: "Accepted: {tokens}",
    sendOnly: "Send only {tokens} on {chain} to this address. Other coins or networks can be lost.",
    qrAlt: "QR code of the payment address",
    waitingPayment: "Waiting for payment… This page checks every 10 seconds.",
    paymentReceived: "Payment received. Creating your vault…",
    vaultReady: "Your vault is ready.",
    cancelRequest: "Cancel request",
    cancelled: "Request cancelled",
    coldSection: "Cold wallet & float",
    coldToOwner: "Send it back to my owner wallet (recommended)",
    coldGoesTo: "Goes to your owner wallet",
    coldOwnerFirst: "Enter the owner wallet address above.",
    coldOtherLabel: "Separate cold wallet",
    coldGuide: "Easiest: one wallet for everything. What the vault doesn't need for payouts goes back to your owner wallet every day. For large amounts, use a hardware wallet or multisig as the owner, or choose a separate cold wallet.",
    coldLabel: "Cold wallet (recommended)",
    coldHint: "Your own wallet (best a hardware wallet or multisig). The vault sends what it doesn't need for payouts here. Only this address can receive it; iCryptoNow can't change it.",
    coldEmptyHint: "You can leave it empty and add it later from your owner wallet.",
    floatLabel: "Keep in vault for payouts",
    floatHint: "About 1–2 days of payouts is enough.",
    autoDaily: "Send the rest to my cold wallet every day at {local} ({hour}:00 UTC)",
    ownerLabel: "Owner wallet address",
    ownerWarning: "This wallet will <b>own</b> the vault: only it can set limits, withdraw and change the cold wallet. Use a wallet you control (MetaMask / TronLink / hardware / multisig), <b>not</b> an exchange deposit address. iCryptoNow cannot change the owner later.",
    submit: "Enable network",
    submitted: "Request sent. We're setting up the vault; this page updates by itself.",
  },

  /** Guided owner actions: what to change → open the owner wallet → confirm in it → wait for the network. */
  guide: {
    progress: "Steps",
    stepWhat: "What to change",
    stepWallet: "Open your owner wallet",
    stepConfirm: "Confirm in your wallet",
    stepWaiting: "Waiting for the network",
    // Step 2
    walletIntro: "We'll ask your wallet to connect, then check that it is the owner of this vault.",
    connect: "Connect wallet",
    connecting: "Waiting for your wallet…",
    noWalletTitle: "No wallet found in this browser",
    installEvm: "Install MetaMask (works for {network})",
    installTron: "Install TronLink (for TRON)",
    openOnOwnerComputer: "Open this page on the computer where your owner wallet is installed, then press Check again.",
    checkAgain: "Check again",
    manualCall: "Or submit the call another way (for example from a multisig):",
    connected: "Connected: {account}",
    ownerOk: "This is the vault owner.",
    networkOk: "Network: {network}",
    wrongAccountTitle: "This isn't the owner wallet",
    wrongAccountBody: "Switch to this account in your wallet, then press Try again:",
    connectedNow: "Connected now: {account}",
    wrongNetwork: "Your wallet couldn't switch to {network}. Choose {network} in your wallet, then press Try again.",
    noAccount: "Your wallet didn't share an account. Unlock it, then press Try again.",
    rejectedConnect: "The connection was cancelled in your wallet. Press Try again when you're ready.",
    // Step 3
    confirmWhat: "When you press the button, your wallet opens and shows a request to the vault contract {vault}. Check it, then press Confirm in the wallet.",
    feeNote: "Confirming costs a small network fee in {gas}, paid from the owner wallet. Keep a little {gas} in it.",
    openWallet: "Open wallet to confirm",
    rejectedSend: "You cancelled the request in your wallet. Nothing was changed.",
    insufficientGas: "There isn't enough {gas} in the owner wallet to pay the network fee. Add a little {gas}, then press Try again.",
    sendFailed: "Your wallet couldn't send it: {message}",
    // Step 4
    waitingIntro: "Your change was sent. The network confirms it and this page checks for you; it usually takes about a minute.",
    checking: "Checking the vault…",
    stillWaiting: "Still waiting. It can take a few minutes when the network is busy; you can close this window and check back later.",
    done: {
      setLimits: "Done: the new limits are active",
      setFloat: "Done: the new float is active",
      setColdWallet: "Done: the new cold wallet is set",
      withdraw: "Done: the withdrawal went through",
      sweepToCold: "Done: swept to the cold wallet",
      pause: "Done: the vault is paused",
      unpause: "Done: the vault is running again",
    },
    help: "Need help? <a>Message us on Telegram @mrho68</a>",
  },

  /** Why a queued payout is held (payout.waiting_code → label; the server's English waiting_reason is shown as the tooltip). */
  waitingCodes: {
    vault_paused: "The vault is paused",
    vault_limits_not_set: "Vault payout limits are not set",
    over_vault_per_payout_limit: "Above the vault's per-payout limit",
    vault_daily_limit_used: "Today's vault payout limit is used up",
    waiting_for_sweep: "Waiting for deposits to be swept into the vault",
    other: "Held by the vault",
  },
};
